import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Chip,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Divider,
  CircularProgress, // Added for spinner
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { BASE_URL } from "./../../../Api/ApiUrls";
import { AuthContext } from "./../../../AuthContext/AuthContext";
import FormsBottomNavbar from "./../../Post Module/FormsBottomNavbar";

const UserVendor = () => {
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!userId) return;

    const fetchVendors = async () => {
      try {
        const response = await fetch(`${BASE_URL}/vendors/`);
        const data = await response.json();
        const userVendors = data.filter(vendor => vendor.user_id == userId);
        setVendors(userVendors);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [userId]);

  // ==================== BLACK SPINNER WHILE LOADING ====================
  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgb(239, 231, 221)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <CircularProgress size={40} thickness={5} sx={{ color: '#000000' }} />
        <Typography variant="h7" sx={{ color: '#000000', fontWeight: 600 }}>
          Loading vendors...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: `
        linear-gradient(145deg, rgba(232,224,208,0.95), rgba(216,204,186,0.95)),
        url('/construction4.png')
      `,
      backgroundSize: 'cover',
      backgroundAttachment: 'fixed',
      pt: '64px', // Adjust for AppBar height
    }}>
      {/* Header */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          background: `
            linear-gradient(145deg,rgb(22, 22, 22),rgb(15, 15, 15)),
            url('/construction5.png')
          `,
          color: 'text.primary',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton 
            edge="start" 
            onClick={() => navigate(-1)}
            sx={{ 
              color: '#ffffff',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': { 
                backgroundColor: 'rgba(255,255,255,0.2)' 
              }
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2, color: 'white' }}>
            My Vendors
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container sx={{ py: 3, flex: 1 }}>
        {vendors.length === 0 ? (
          <Card sx={{ borderRadius: '12px', boxShadow: 3, p: 3, textAlign: 'center' }}>
            <Typography variant="h6">No vendors found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You haven't added any vendors yet.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {vendors.map((vendor) => (
              <Grid item xs={12} sm={6} md={2} key={vendor.vendor_id}>
                <Card sx={{ 
                  borderRadius: '12px', 
                  overflow: 'hidden', 
                  boxShadow: 3, 
                  position: 'relative',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}>
                  {/* Edit Icon Button positioned at top-right */}
                  <IconButton
                    onClick={() => navigate(`/user-edit-vendor/${vendor.vendor_id}`)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      },
                      zIndex: 1
                    }}
                  >
                    <EditIcon />
                  </IconButton>

                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                      <Avatar
                        alt={vendor.name || "Vendor"}
                        src={vendor.profile ? `${BASE_URL}${vendor.profile}` : ""}
                        sx={{
                          width: 120,
                          height: 120,
                          mb: 3,
                          border: '4px solid white',
                          boxShadow: 3
                        }}
                      />
                      
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {vendor.name || "Loading..."}
                      </Typography>
                      
                      <Chip 
                        label={vendor.profession} 
                        color="primary" 
                        size="medium"
                        sx={{ mb: 2 }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ p: 2 }}>
                      {[
                        { label: 'Experience', value: `${vendor.experience} year${vendor.experience !== 1 ? 's' : ''}` || "Not provided" },
                        { label: 'Mobile', value: vendor.mobile || "Not provided" },
                        { label: 'Email', value: vendor.email || "Not provided" },
                        { label: 'Address', value: isMobile ? 
                            `${vendor.address?.substring(0, 30)}...` : 
                            `${vendor.address?.substring(0, 50)}...` || "Not provided" },
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {item.label}:
                          </Typography>
                          <Typography variant="body1">
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Box sx={{ height: '70px' }} />
      
      {/* Bottom Navigation */}
      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default UserVendor;